/**
 * `<ContactActionsMenu>` — kebab menu used in the contacts table row and
 * detail page header. Items are gated by the contact's current status:
 * deleted contacts have no recoverable actions in v1 (FD § 2.1).
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@cometchat-ai/ui-dashboard";
import { MoreHorizontal, Pencil, Power, Trash2, UserRound } from "../icons";
import { useTranslation } from "react-i18next";

import type { Contact } from "../../lib/api/types";

export interface ContactActionsMenuProps {
  contact: Pick<Contact, "id" | "isDisabled" | "deletedAt">;
  /** Open the contact's profile (detail page). */
  onView?: () => void;
  onEdit?: () => void;
  onDisable?: () => void;
  onEnable?: () => void;
  onDelete?: () => void;
  /** Hide the trigger entirely (e.g. on deleted contacts where no actions apply). */
  hidden?: boolean;
}

export function ContactActionsMenu({
  contact,
  onView,
  onEdit,
  onDisable,
  onEnable,
  onDelete,
  hidden,
}: ContactActionsMenuProps) {
  const { t } = useTranslation("contacts");
  if (hidden || contact.deletedAt) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("actions.openMenu")}
          data-testid={`contact-actions-trigger-${contact.id}`}
          className="h-6 w-6 shrink-0 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:scale-100 data-[state=open]:bg-muted data-[state=open]:text-foreground"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onView ? (
          <DropdownMenuItem onSelect={onView} data-testid={`contact-action-view-${contact.id}`}>
            <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("actions.view")}
          </DropdownMenuItem>
        ) : null}
        {onEdit ? (
          <DropdownMenuItem onSelect={onEdit} data-testid={`contact-action-edit-${contact.id}`}>
            <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("actions.edit")}
          </DropdownMenuItem>
        ) : null}
        {contact.isDisabled
          ? onEnable && (
              <DropdownMenuItem
                onSelect={onEnable}
                data-testid={`contact-action-enable-${contact.id}`}
              >
                <Power className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("actions.enable")}
              </DropdownMenuItem>
            )
          : onDisable && (
              <DropdownMenuItem
                onSelect={onDisable}
                data-testid={`contact-action-disable-${contact.id}`}
              >
                <Power className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("actions.disable")}
              </DropdownMenuItem>
            )}
        {onDelete ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:text-destructive"
              data-testid={`contact-action-delete-${contact.id}`}
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("actions.delete")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
